import React, { useContext } from 'react'
import { FormLayoutRow, Well } from '@pushly/aqe/lib/components'
import { Radio } from 'antd'
import { PromptVisitorConditions } from '@pushly/models/lib/structs/prompts/prompt-visitor-conditions'
import { VisitorCondition } from '../types'
import { TimeOnPageConditionEditor } from './visitor-conditions/time-on-page-condition-editor'
import { SessionPageViewsConditionEditor } from './visitor-conditions/session-page-views-condition-editor'
import { SessionTimeOnSiteCondition } from './visitor-conditions/session-time-on-site-condition-editor'
import {
    DEFAULT_SESSION_PAGE_VIEWS,
    DEFAULT_SESSION_TIME_ON_SITE_SECONDS,
    DEFAULT_TIME_ON_PAGE_SECONDS,
} from '../constants'
import { PromptEditorDispatchContext } from '../reducer'
import { PromptEditorContext } from '../context'

export const PromptVisitorConditionsEditor = () => {
    const dispatch = useContext(PromptEditorDispatchContext)
    const { loading, selectedPrompt, selectedPromptIdx } = useContext(PromptEditorContext)

    const visitorConditions = selectedPrompt?.conditions?.visitor

    let selectedConditionType: VisitorCondition = VisitorCondition.EVERYONE
    if (visitorConditions) {
        if ('timeOnPageSeconds' in visitorConditions) {
            selectedConditionType = VisitorCondition.TIME_ON_PAGE
        } else if ('sessionPageViews' in visitorConditions) {
            selectedConditionType = VisitorCondition.SESSION_PAGE_VIEWS
        } else if ('sessionTimeOnSiteSeconds' in visitorConditions) {
            selectedConditionType = VisitorCondition.SESSION_TIME_ON_SITE
        }
    }

    function setVisitorConditions(conditionsUpdate: PromptVisitorConditions | undefined): void {
        if (loading) return

        const update = selectedPrompt.clone()
        update.conditions ??= new PromptVisitorConditions()
        update.conditions.visitor = conditionsUpdate

        dispatch({ type: 'set-prompt', idx: selectedPromptIdx, prompt: update })
    }

    return (
        <Well className="visitor-conditions-well" title="Visitor Conditions" hideFooter={true} mode="ghost">
            <p>
                By default this prompt will be shown to the visitor immediately. You may choose to delay the prompt
                display by enabling one of the below conditions.
            </p>

            <FormLayoutRow
                loading={loading}
                skeleton={{ width: 420 }}
                className="visitor-condition-type"
                layout="vertical"
                label="Users will receive this prompt after"
                value={
                    <Radio.Group
                        key={selectedPromptIdx}
                        size="small"
                        buttonStyle="solid"
                        defaultValue={selectedConditionType}
                        onChange={(ev) => {
                            const nextConditionType = ev.target.value as VisitorCondition
                            let nextCondition: PromptVisitorConditions | undefined

                            switch (nextConditionType) {
                                case VisitorCondition.TIME_ON_PAGE: {
                                    nextCondition = PromptVisitorConditions.from({
                                        timeOnPageSeconds: DEFAULT_TIME_ON_PAGE_SECONDS,
                                    })
                                    break
                                }
                                case VisitorCondition.SESSION_PAGE_VIEWS: {
                                    nextCondition = PromptVisitorConditions.from({
                                        sessionPageViews: DEFAULT_SESSION_PAGE_VIEWS,
                                    })
                                    break
                                }
                                case VisitorCondition.SESSION_TIME_ON_SITE: {
                                    nextCondition = PromptVisitorConditions.from({
                                        sessionTimeOnSiteSeconds: DEFAULT_SESSION_TIME_ON_SITE_SECONDS,
                                    })
                                    break
                                }
                            }

                            setVisitorConditions(nextCondition)
                        }}
                    >
                        <Radio.Button value={VisitorCondition.EVERYONE}>Page Load</Radio.Button>
                        <Radio.Button value={VisitorCondition.TIME_ON_PAGE}>Time on Page</Radio.Button>
                        <Radio.Button value={VisitorCondition.SESSION_PAGE_VIEWS}># of Page Views</Radio.Button>
                        <Radio.Button value={VisitorCondition.SESSION_TIME_ON_SITE}>Time on Site</Radio.Button>
                    </Radio.Group>
                }
            />

            {selectedConditionType === VisitorCondition.TIME_ON_PAGE && (
                <TimeOnPageConditionEditor value={visitorConditions} onChange={setVisitorConditions} />
            )}
            {selectedConditionType === VisitorCondition.SESSION_PAGE_VIEWS && (
                <SessionPageViewsConditionEditor value={visitorConditions} onChange={setVisitorConditions} />
            )}
            {selectedConditionType === VisitorCondition.SESSION_TIME_ON_SITE && (
                <SessionTimeOnSiteCondition value={visitorConditions} onChange={setVisitorConditions} />
            )}
        </Well>
    )
}
